import { HttpClient } from 'api/http-client';
import { DEFAULT_DATE_FORMAT } from 'api/models';
import { ApiTaskStatusesWithCount } from 'api/sprint-report/types';
import { getValueFromDateRange } from 'components/organisms/projects-report/common/range-picker/types';
import {
  CompleteSprintPayload,
  SprintInfoPayload,
  SprintRemoveTaskPayload,
  SprintUpdatePayload,
} from 'components/organisms/sprint-planning/types';
import { constructUrl } from 'utils/http-utilts';
import { ApiFilter } from 'utils/simple-search-filter/simple-search-filter';
import {
  GetSprintListPayload,
  PaginatedSprintParams,
  PaginatedSprintResponse,
  Sprint,
  SprintConfig,
  SprintsList,
  TasksInSprintForExternalUser,
} from './types';

class SprintPlanningHttpClient extends HttpClient {
  public async getSprintConfiguration(): Promise<SprintConfig> {
    return this.get('/sprint-planning/configuration');
  }

  public async updateConfig(newConfig: SprintConfig): Promise<SprintConfig> {
    return this.put('/sprint-planning/configuration', newConfig);
  }

  public async createSprint(payload: SprintInfoPayload): Promise<Sprint> {
    return this.post('/sprints', payload);
  }

  public async getSprint(sprintId: number): Promise<Sprint> {
    return this.get(`/sprints/${sprintId}`);
  }

  public async getTaskCountInSprint(sprintId: number): Promise<number> {
    return this.get(`/tasks/get-tasks-with-current-sprint/${sprintId}`);
  }

  public getPaginatedSprintList = async ({
    startRow,
    endRow,
    filter,
    sort,
    search,
    range,
  }: PaginatedSprintParams): Promise<PaginatedSprintResponse> => {
    const [startDate, endDate] = range
      ? getValueFromDateRange(range).map((x) => x.format(DEFAULT_DATE_FORMAT))
      : [];

    const filterAsString = filter?.criteria?.length
      ? ApiFilter.toQueryStringFormat(filter.criteria)
      : undefined;

    const url = constructUrl({
      baseURL: '/sprints',
      params: {
        startDate,
        endDate,
        sort,
        offset: startRow,
        limit: endRow - startRow,
        match: filter.type,
      },
      paramsToAddWithoutKeys: [search, filterAsString],
    });

    return this.get<PaginatedSprintResponse>(url).then((response) => response);
  };

  public async updateSprint(
    sprintId: number,
    payload: SprintUpdatePayload,
    isSync?: boolean,
  ): Promise<Sprint> {
    return this.put(
      `/sprints/${sprintId}${isSync ? `?isSync=${true}` : ''}`,
      payload,
    );
  }

  public async addTasksToSprint(
    sprintId: number,
    payload: number[],
  ): Promise<Sprint> {
    return this.post(`/sprints/${sprintId}/add-tasks`, { taskIds: payload });
  }

  public async getSprintsList({
    startDate,
    endDate,
    projectIds,
    inComplete,
    isAlreadyAddedToPhase = true,
    sprintId,
  }: GetSprintListPayload): Promise<SprintsList> {
    const url = constructUrl({
      baseURL: 'sprints/get-sprints/light/',
      params: {
        sprintId,
        inComplete,
        projectIds,
        startDate: !inComplete ? startDate : undefined,
        endDate: !inComplete ? endDate : undefined,
        isAlreadyAddedToPhase: !isAlreadyAddedToPhase
          ? isAlreadyAddedToPhase
          : undefined,
      },
    });

    return this.get(`${url}`);
  }

  public async updateSprintStatus({ sprintId, payload }): Promise<void> {
    return this.put(`/sprints/${sprintId}`, payload);
  }

  public getTaskStatusesWithCount = async (
    sprintId: number,
  ): Promise<ApiTaskStatusesWithCount[]> => {
    return this.get<ApiTaskStatusesWithCount[]>(
      `sprints/${sprintId}/get-task-statuses`,
    );
  };

  public async removeTaskFromSprint({
    sprintId,
    payload,
  }: SprintRemoveTaskPayload): Promise<void> {
    return this.post(`/sprints/${sprintId}/remove-task`, payload);
  }

  public async getTasksInSprintForExternalUser({
    sprintId,
    projectId,
  }: {
    sprintId: number;
    projectId: number;
  }): Promise<TasksInSprintForExternalUser> {
    return this.get(`projects/${projectId}/sprints/${sprintId}/tasks`);
  }

  public async deleteSprint({ sprintId }: { sprintId: number }): Promise<void> {
    return this.delete(`/sprints/${sprintId}`);
  }

  public async completeSprint({
    sprintId,
    payload,
  }: CompleteSprintPayload): Promise<Sprint> {
    return this.post(`/sprints/${sprintId}/complete-sprint`, payload);
  }

  public async sprintsCount(): Promise<number> {
    return this.get('/sprints/total-count');
  }
}

export const SprintPlanningAPI = new SprintPlanningHttpClient();
